import React, {useEffect, useMemo, useState} from 'react';
import {Button, Checkbox, HStack, Input, Spinner, VStack} from '@chakra-ui/react';
import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {Field, FileInput, FilesInput, LanguageField, NestedSelect, RichText} from "@/components";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";

export const CreateOrEditArticleTag = (props) => {
    const tagId = props?.id;

    const [state, setState] = useState(-1);
    const {getLangObject, language, t} = useLanguage();
    const [articleTag, setArticleTag] = useState(null);
    const [tags, setTags] = useState(null);
    const api = useApi();
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            label: getLangObject(),
            entityType: 'article',
            thumbnail: null
        },
        {
            label: {
                custom: {
                    function: (value) => {
                        return Object.values(value).every((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
        }
    );

    useEffect(() => {
        setState(() => 0);
        api.ApiService.get('tag', {
            getAll: 'bool:true'
        })
            .then((res) => {
                if (res?.data?.type === 'success' && res?.data?.value) {
                    let tmp = objectUtil.plainToTree(res.data.value ?? [], 'id', 'parent');
                    tmp = objectUtil.treeToPlain(tmp, 'children', 'id', 'parent');
                    setTags(() => tmp);
                }
            })
        if (tagId) {
            api.ApiService.getOne('tag', tagId, {
                attributes: ['id', 'label', 'thumbnail'],
            })
                .then((res) => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        setArticleTag(() => res.data.value);
                    }
                })
        }
    }, []);

    useEffect(() => {
        if (articleTag !== null) {
            handleInputChanges(articleTag);
        }
        /** If tags are retrieved, and we're in create mode
         * or resource and tags are retrieved, and we're in edit mode
         */
        if ((!tagId || articleTag !== null) && tags !== null) {
            setState(() => 1)
        }
    }, [tagId, articleTag, tags]);

    const tagsTree = useMemo(() => {
        return (tags ?? []).map((e) => ({...e, value: e.id, label: e?.[`${language}Label`] ?? ''}))
    }, [tags, language]);

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                state === 1 ?
                    <VStack overflow="hidden" flex={1} gap="5" p="5" w="100%" align="center" justify="flex-start">
                        <VStack flex={1} px={2} w="100%" h="80vh" position="relative" overflow="auto">
                            <HStack w="100%" gap="2rem">
                                <FileInput
                                    hasPreview
                                    onChange={(file) => handleInputChange('thumbnail', file)}
                                    file={formData.thumbnail}
                                    minH="20vw"
                                    maxH="20vw"
                                    minW="20vw"
                                    maxW="20vw"
                                />
                                <LanguageField
                                    id="label"
                                    errors={formFieldErrors.label}
                                    label={t('wrappers.mandatoryLabel', { value: t('fields.label') })}
                                    renderElement={(lang) => <Input
                                        value={formData.label[lang]}
                                        onChange={(e) => {
                                            handleInputChange('label', {...formData.label, [lang]: e?.target?.value ?? ''})
                                        }}
                                        placeholder={t('resources.resourceTitle')}
                                    />}
                                />
                            </HStack>
                        </VStack>
                    </VStack>
                    :
                    <VStack flex={1} w="100%" align="center" justify="flex-start">
                        <Spinner size="lg"/>
                    </VStack>
            }
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: '1rem 1.5rem'}}
                onClick={() => handleFormSubmit(async (data) => {
                    if (articleTag) {
                        return api.ApiService.update('tag', articleTag.id, data);
                    }
                    return api.ApiService.create('tag', data);
                }, props?.successCallback)
                }
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditArticleTag;
